import { action, computed, observable, reaction, makeObservable, runInAction } from 'mobx';
import { X5LessonSnapshot, X8BlockSnapshot, X9ContentSnapshot } from '../../../domain';
import { Disposers, HttpPromiseState, logger } from '../../../lib/helpers';
import { Homework } from '../../homework/homework-models';
import { HomeworkUtils } from '../../homework/homework-utils';
import { HomeworkLoaders } from '../../homework/stores/homework-loaders';
import { ExamHubStore } from '../../exam/stores/ExamHubStore';
import { ExamLoaders } from '../ExamLoaders';
import Swal from 'sweetalert2';
import i18next from 'i18next';

export interface ExamMistakeAnswerPart {
  text: string;
  isCorrect: boolean;
}

export interface ExamMistake {
  blockId: number;
  blockName: string;
  contentId: number;
  contentType: string;
  question: string;
  studentAnswer: string;
  correctAnswer: string;
  /** Parts of student answer for per-item green highlighting (correct parts in green) */
  studentAnswerParts?: ExamMistakeAnswerPart[];
}

export interface ExamResult {
  totalBal: number;
  maxBal: number;
  totalQuestions: number;
  correctCount: number;
  incorrectCount: number;
  mistakes: ExamMistake[];
}

export class ExamStudentSceneStore {
  @observable.ref
  homeworkLoader: HttpPromiseState<Homework> | null;

  hubStore: ExamHubStore;

  @observable.ref
  snapshotState: HttpPromiseState<X5LessonSnapshot> | null;

  @observable.ref
  snapshot: X5LessonSnapshot | null;

  // Timer state
  @observable
  remainingSeconds: number | null = null;

  @observable
  isExamFinished: boolean = false;

  @observable
  examResult: ExamResult | null = null;

  private timerInterval: ReturnType<typeof setInterval> | null = null;
  /** When exam response had ServerTime: server time (Unix s) at receive moment — used to compute server "now" and avoid client clock drift. */
  private timerSyncServerTime: number | null = null;
  /** Client time (Unix s) when we received the exam response; together with timerSyncServerTime gives server "now". */
  private timerSyncClientTime: number | null = null;
  private readonly disposers = new Disposers();

  constructor(readonly examId: number) {
    makeObservable(this);
    this.homeworkLoader = null;

    this.hubStore = new ExamHubStore(examId, 'Student');
    this.disposers.add(this.hubStore);

    this.snapshotState = this.snapshot = null;
    this.setup();
  }

  private setup() {
    this.disposers.add(
      reaction(
        () => this.homeworkLoader?.value,
        this.onHomeworkLoaded),
      reaction(
        () => this.snapshotState?.value,
        this.onLessonLoaded),
     
    );
  }

  @computed
  get homework(): Homework | null {
    return this.homeworkLoader?.value ?? null;
  }

  @computed
  get hasTimeLimit(): boolean {
    const hw = this.homework;
    return hw?.TimeLimitMinutes != null && hw.TimeLimitMinutes > 0;
  }

  @computed
  get isTimedOut(): boolean {
    return this.remainingSeconds !== null && this.remainingSeconds <= 0;
  }

  @computed
  get canEditAnswers(): boolean {
    return !this.isExamFinished && !this.isTimedOut;
  }

  @action
  private onHomeworkLoaded = () => {
    const state = this.homeworkLoader;
    if (state === null || !state.isResolved)
      return;
    
    const homework = state.value;
    
    // Check if exam is already finished
    if (homework.Status === 'Accepted' || homework.Status === 'Rejected') {
      this.isExamFinished = true;
      // Load existing result
      if (homework.Bal != null) {
        const mistakes = homework.MistakesJson ? JSON.parse(homework.MistakesJson) : [];
        this.examResult = {
          totalBal: homework.Bal,
          maxBal: 0, // Will be calculated later in onLessonLoaded
          totalQuestions: 0, // Will be recalculated in onLessonLoaded
          correctCount: 0,
          incorrectCount: mistakes.length,
          mistakes
        };
      }
    }
    
    // Start timer if has time limit and not finished
    if (!this.isExamFinished && homework.TimeLimitMinutes && homework.StartedAt) {
      this.startTimer(homework.TimeLimitMinutes, homework.StartedAt, homework.ServerTime ?? undefined);
    }
    
    this.hubStore.start();
    this.loadLessonExam();
  };

  @action
  private startTimer = (timeLimitMinutes: number, startedAtUnix: number, serverTimeFromResponse?: number) => {
    const endTimeUnix = startedAtUnix + timeLimitMinutes * 60;
    if (serverTimeFromResponse != null && Number.isFinite(serverTimeFromResponse)) {
      this.timerSyncServerTime = serverTimeFromResponse;
      this.timerSyncClientTime = Math.floor(Date.now() / 1000);
    } else {
      this.timerSyncServerTime = null;
      this.timerSyncClientTime = null;
    }

    const updateTimer = () => {
      let nowUnix: number;
      if (this.timerSyncServerTime != null && this.timerSyncClientTime != null) {
        nowUnix = this.timerSyncServerTime + (Math.floor(Date.now() / 1000) - this.timerSyncClientTime);
      } else {
        nowUnix = Math.floor(Date.now() / 1000);
      }
      const remaining = Math.max(0, endTimeUnix - nowUnix);

      runInAction(() => {
        this.remainingSeconds = remaining;

        if (remaining <= 0 && !this.isExamFinished) {
          this.handleTimeOut();
        }
      });
    };

    updateTimer();
    this.timerInterval = setInterval(updateTimer, 1000);
  };

  @action
  private handleTimeOut = () => {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.finishExam(true);
  };

  @action
  loadLessonExam = () => {
    if (this.snapshotState?.isPending)
      return;
    this.snapshotState = HomeworkLoaders.Student.getLessonExamSnapshot(this.examId);
  };

  @action
  loadExam = () => {
    if (this.homeworkLoader?.isPending)
      return;
    this.homeworkLoader = HomeworkLoaders.Student.getExam(this.examId);
  };

  @action
  private onLessonLoaded = () => {
    const state = this.snapshotState;
    if (state === null || !state.isResolved)
      return;
    this.snapshot = HomeworkUtils.prepareLesson(state.value);
    
    // Recalculate stats from snapshot if we have existing result (reopened exam)
    if (this.examResult) {
      const stats = this.calculateSnapshotStats();
      this.examResult.maxBal = stats.maxBal;
      this.examResult.totalQuestions = stats.totalQuestions;
      this.examResult.correctCount = stats.totalQuestions - this.examResult.mistakes.length;
      this.examResult.incorrectCount = this.examResult.mistakes.length;
    }
  };

  private calculateSnapshotStats(): { maxBal: number; totalQuestions: number } {
    if (!this.snapshot) return { maxBal: 0, totalQuestions: 0 };
    
    let maxBal = 0;
    let totalQuestions = 0;
    for (const page of this.snapshot.PageSnapshots ?? []) {
      for (const pushableBlock of page.PushableBlockSnapshots ?? []) {
        for (const block of pushableBlock.BlockSnapshots ?? []) {
          if (block.Bal) {
            maxBal += block.Bal;
          }
          const gradable = block.ContentSnapshots?.filter(c => this.isGradableContent(c.ContentType)) ?? [];
          totalQuestions += gradable.length;
        }
      }
    }
    return { maxBal, totalQuestions };
  }

  @action
  confirmFinishExam = async () => {
    const t = i18next.t.bind(i18next);
    const result = await Swal.fire({
      title: t('exam.finish.confirm-title'),
      text: t('exam.finish.confirm-text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('exam.finish.confirm-yes'),
      cancelButtonText: t('exam.finish.confirm-cancel')
    });

    if (result.isConfirmed) {
      this.finishExam(false);
    }
  };

  @action
  finishExam = async (isTimeout: boolean) => {
    if (this.isExamFinished) return;
    
    try {
      // Calculate score and collect mistakes
      const result = this.calculateResult();
      
      // Send to backend
      await ExamLoaders.finishExam({
        examId: this.examId,
        totalBal: result.totalBal,
        mistakesJson: JSON.stringify(result.mistakes)
      });
      
      runInAction(() => {
        this.isExamFinished = true;
        this.examResult = result;
        
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      });
      
      const t = i18next.t.bind(i18next);
      if (isTimeout) {
        Swal.fire({
          title: t('exam.finish.timeout-title'),
          text: t('exam.finish.success-text', { score: result.totalBal, max: result.maxBal }),
          icon: 'info'
        });
      } else {
        Swal.fire({
          title: t('exam.finish.success-title'),
          text: t('exam.finish.success-text', { score: result.totalBal, max: result.maxBal }),
          icon: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to finish exam:', error);
      const t = i18next.t.bind(i18next);
      Swal.fire({
        title: t('exam.finish.error-title'),
        text: t('exam.finish.error-text'),
        icon: 'error'
      });
    }
  };

  private calculateResult(): ExamResult {
    const mistakes: ExamMistake[] = [];
    let totalBal = 0;
    let maxBal = 0;
    let totalQuestions = 0;
    let correctCount = 0;
    let incorrectCount = 0;
    
    if (!this.snapshot) {
      return { totalBal: 0, maxBal: 0, totalQuestions: 0, correctCount: 0, incorrectCount: 0, mistakes: [] };
    }
    
    const keyValues = this.hubStore.examKeyValues;
    
    for (const page of this.snapshot.PageSnapshots ?? []) {
      for (const pushableBlock of page.PushableBlockSnapshots ?? []) {
        for (const block of pushableBlock.BlockSnapshots ?? []) {
          const blockBal = block.Bal ?? 0;
          maxBal += blockBal;

          const contents = block.ContentSnapshots?.filter(c => this.isGradableContent(c.ContentType)) ?? [];
          if (contents.length === 0) continue;

          const balPerContent = contents.length > 0 ? blockBal / contents.length : 0;
          
          for (const content of contents) {
            totalQuestions++;
            const checkResult = this.checkContent(content, keyValues);
            // Partial credit: add points for correct part of content; ratio in [0, 1]
            const ratio = Math.max(0, Math.min(1, checkResult.scoreRatio));
            totalBal += balPerContent * ratio;

            if (checkResult.isCorrect) {
              correctCount++;
            } else {
              incorrectCount++;
              mistakes.push({
                blockId: block.Id,
                blockName: `Block ${block.Id}`,
                contentId: content.Id,
                contentType: content.ContentType,
                question: checkResult.question,
                studentAnswer: checkResult.studentAnswer,
                correctAnswer: checkResult.correctAnswer,
                studentAnswerParts: checkResult.answerParts
              });
            }
          }
        }
      }
    }

    return {
      totalBal: Math.round(totalBal),
      maxBal,
      totalQuestions,
      correctCount,
      incorrectCount,
      mistakes
    };
  }

  private isGradableContent(contentType: string): boolean {
    return [
      'matching-question',
      'select-question',
      'gap-filling',
      'multiple-choice-question',
      'grouping-exercise'
    ].includes(contentType);
  }

  private checkContent(content: X9ContentSnapshot, keyValues: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    const state = keyValues.getContentState(content.Id);
    const model = JSON.parse(content.Contents);

    switch (content.ContentType) {
      case 'multiple-choice-question':
        return this.checkMcq(model, state);
      case 'gap-filling':
        return this.checkGapFilling(model, state);
      case 'matching-question':
        return this.checkMatching(model, state);
      case 'select-question':
        return this.checkSelectQuestion(model, state);
      case 'grouping-exercise':
        return this.checkGroupingExercise(model, state);
      default:
        return { isCorrect: true, question: '', studentAnswer: '', correctAnswer: '', scoreRatio: 1, answerParts: undefined };
    }
  }

  private checkMcq(model: any, state: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    const noAnswer = 'No answer';
    if (!state) {
      return {
        isCorrect: false,
        question: model.choices?.map((c: any) => c.label).join(', ') ?? '',
        studentAnswer: noAnswer,
        correctAnswer: model.choices?.filter((c: any) => c.selected).map((c: any) => c.label).join(', ') ?? '',
        scoreRatio: 0,
        answerParts: [{ text: noAnswer, isCorrect: false }]
      };
    }
    
    const correctIndices = model.choices
      ?.map((c: any, i: number) => c.selected ? i : -1)
      .filter((i: number) => i >= 0) ?? [];
    
    if (model.behavior === 'single-select') {
      const isCorrect = correctIndices.includes(state.selectedIndex);
      const studentAnswer = state.selectedIndex != null ? model.choices[state.selectedIndex]?.label : noAnswer;
      return {
        isCorrect,
        question: model.choices?.map((c: any) => c.label).join(', ') ?? '',
        studentAnswer,
        correctAnswer: model.choices?.filter((c: any) => c.selected).map((c: any) => c.label).join(', ') ?? '',
        scoreRatio: isCorrect ? 1 : 0,
        answerParts: [{ text: studentAnswer, isCorrect }]
      };
    }
    
    // multi-select: partial credit = correct choices / total correct count
    const selectedIndices = state.states
      ?.map((s: any, i: number) => s.selected ? i : -1)
      .filter((i: number) => i >= 0) ?? [];
    const correctSelected = correctIndices.filter((i: number) => selectedIndices.includes(i)).length;
    const totalCorrect = correctIndices.length;
    const isCorrect = totalCorrect > 0 && correctSelected === totalCorrect && selectedIndices.length === totalCorrect;
    const scoreRatio = totalCorrect > 0 ? correctSelected / totalCorrect : 0;
    const studentAnswer = selectedIndices.map((i: number) => model.choices[i]?.label).join(', ') || noAnswer;
    return {
      isCorrect,
      question: model.choices?.map((c: any) => c.label).join(', ') ?? '',
      studentAnswer,
      correctAnswer: correctIndices.map((i: number) => model.choices[i]?.label).join(', '),
      scoreRatio,
      answerParts: [{ text: studentAnswer, isCorrect }]
    };
  }

  private checkGapFilling(model: any, state: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    // Content stores save as state.states (KbtGapFillingState / DndGapFillingState), not gapStates
    const gapStates = state?.states ?? state?.gapStates;
    if (!state || !gapStates) {
      return {
        isCorrect: false,
        question: 'Gap filling',
        studentAnswer: 'No answer',
        correctAnswer: this.extractGapFillingAnswers(model),
        scoreRatio: 0,
        answerParts: [{ text: 'No answer', isCorrect: false }]
      };
    }

    // DnD stores item ids (e.g. "0/1") in filledWith; map id -> word for display and comparison
    const gapIdToValue: { [id: string]: string } = {};
    const paragraphs = model.paragraphs ?? [];
    for (let pIdx = 0; pIdx < paragraphs.length; pIdx++) {
      const items = paragraphs[pIdx].items ?? [];
      for (let iIdx = 0; iIdx < items.length; iIdx++) {
        const it = items[iIdx];
        if (it?.type === 'gap' && it.value != null)
          gapIdToValue[`${pIdx}/${iIdx}`] = it.value;
      }
    }
    const isDnd = model.behavior === 'drag-and-drop';
    
    let correctCount = 0;
    let totalCount = 0;
    const studentAnswers: string[] = [];
    const correctAnswers: string[] = [];
    const answerParts: ExamMistakeAnswerPart[] = [];
    
    for (const paragraph of paragraphs) {
      for (const item of paragraph.items ?? []) {
        if (item.type === 'gap') {
          totalCount++;
          const key = `${paragraphs.indexOf(paragraph)}/${paragraph.items.indexOf(item)}`;
          const gapState = gapStates[key];
          const filledWith = gapState?.filledWith != null ? String(gapState.filledWith) : '';
          // DnD: filledWith is item id → show word; Kbt: filledWith is typed text
          const displayText = isDnd
            ? (gapIdToValue[filledWith] ?? filledWith)
            : filledWith;
          const studentAnswer = displayText.trim().toLowerCase();
          const correctAnswer = item.value?.trim().toLowerCase() ?? '';
          const isItemCorrect = studentAnswer === correctAnswer;
          if (isItemCorrect) correctCount++;
          
          studentAnswers.push(displayText || '');
          correctAnswers.push(item.value ?? '');
          answerParts.push({ text: displayText || '(no answer)', isCorrect: isItemCorrect });
        }
      }
    }
    
    const scoreRatio = totalCount > 0 ? correctCount / totalCount : 0;
    return {
      isCorrect: totalCount > 0 && correctCount === totalCount,
      question: 'Gap filling',
      studentAnswer: studentAnswers.join(', ') || 'No answer',
      correctAnswer: correctAnswers.join(', '),
      scoreRatio,
      answerParts
    };
  }

  private extractGapFillingAnswers(model: any): string {
    const answers: string[] = [];
    for (const paragraph of model.paragraphs ?? []) {
      for (const item of paragraph.items ?? []) {
        if (item.type === 'gap') {
          answers.push(item.value ?? '');
        }
      }
    }
    return answers.join(', ');
  }

  private checkMatching(model: any, state: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    // Content stores save DataPairStates (PascalCase), not dataPairStates
    const dataPairStates = state?.DataPairStates ?? state?.dataPairStates;
    const dataPairs = model?.dataPairs ?? [];
    if (!state || !dataPairStates || !Array.isArray(dataPairStates)) {
      return {
        isCorrect: false,
        question: 'Matching',
        studentAnswer: 'No answer',
        correctAnswer: dataPairs.length > 0 ? this.formatMatchingPairs(dataPairs, dataPairs.map((_: any, i: number) => i)) : 'Correct matching',
        scoreRatio: 0,
        answerParts: [{ text: 'No answer', isCorrect: false }]
      };
    }
    
    let correctCount = 0;
    const total = dataPairStates.length;
    const studentMatches = dataPairStates.map((s: any) => s.matchedTo);
    const correctMatches = dataPairs.map((_: any, i: number) => i);
    const answerParts: ExamMistakeAnswerPart[] = dataPairs.map((pair: any, leftIdx: number) => {
      const rightIdx = studentMatches[leftIdx];
      const right = rightIdx != null && dataPairs[rightIdx] ? dataPairs[rightIdx].rightContent : '—';
      const isCorrect = dataPairStates[leftIdx].matchedTo === leftIdx;
      if (isCorrect) correctCount++;
      const text = this.isMatchingImageUrl(pair.leftContent) ? right : `${pair.leftContent} → ${right}`;
      return { text, isCorrect };
    });
    const scoreRatio = total > 0 ? correctCount / total : 0;
    const studentAnswerStr = dataPairs.length > 0
      ? this.formatMatchingPairs(dataPairs, studentMatches)
      : (correctCount === total ? 'Correct' : 'Incorrect matching');
    const correctAnswerStr = dataPairs.length > 0
      ? this.formatMatchingPairs(dataPairs, correctMatches)
      : 'Each pair matched correctly';
    
    return {
      isCorrect: total > 0 && correctCount === total,
      question: 'Matching',
      studentAnswer: studentAnswerStr,
      correctAnswer: correctAnswerStr,
      scoreRatio,
      answerParts
    };
  }

  /** True if left content is an image URL (e.g. /api/uploads/.../image) — do not show in results */
  private isMatchingImageUrl(s: string): boolean {
    return typeof s === 'string' && (s.startsWith('/api/uploads') || s.startsWith('/upload')) && (s.includes('/image') || /\/image\/?$/i.test(s));
  }

  /** Format matching pairs: one row per pair; omit image URLs so only text (rightContent) is shown */
  private formatMatchingPairs(dataPairs: Array<{ leftContent: string; rightContent: string }>, rightIndices: (number | null)[]): string {
    return dataPairs
      .map((pair: any, leftIdx: number) => {
        const rightIdx = rightIndices[leftIdx];
        const right = rightIdx != null && dataPairs[rightIdx] ? dataPairs[rightIdx].rightContent : '—';
        return this.isMatchingImageUrl(pair.leftContent) ? right : `${pair.leftContent} → ${right}`;
      })
      .join('\n');
  }

  private checkSelectQuestion(model: any, state: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    // select-question has paragraphs with select items
    // state: { states: { "0/0": { selected: number|null, checked: boolean }, ... } }
    if (!state || !state.states) {
      return {
        isCorrect: false,
        question: 'Select question',
        studentAnswer: 'No answer',
        correctAnswer: this.extractSelectQuestionAnswers(model),
        scoreRatio: 0,
        answerParts: [{ text: 'No answer', isCorrect: false }]
      };
    }

    let correctCount = 0;
    let totalCount = 0;
    const studentAnswers: string[] = [];
    const correctAnswers: string[] = [];
    const answerParts: ExamMistakeAnswerPart[] = [];

    for (let pIdx = 0; pIdx < (model.paragraphs ?? []).length; pIdx++) {
      const paragraph = model.paragraphs[pIdx];
      for (let iIdx = 0; iIdx < (paragraph.items ?? []).length; iIdx++) {
        const item = paragraph.items[iIdx];
        if (item.type !== 'select') continue;

        totalCount++;
        const itemId = `${pIdx}/${iIdx}`;
        const selectState = state.states[itemId];
        const correctIndex = item.choices?.findIndex((c: any) => c.selected) ?? -1;

        let partText: string;
        let isItemCorrect: boolean;
        if (!selectState || selectState.selected === null) {
          partText = '(no answer)';
          isItemCorrect = false;
          studentAnswers.push(partText);
        } else {
          isItemCorrect = selectState.selected === correctIndex;
          if (isItemCorrect) correctCount++;
          partText = item.choices?.[selectState.selected]?.label ?? '?';
          studentAnswers.push(partText);
        }
        correctAnswers.push(item.choices?.[correctIndex]?.label ?? '?');
        answerParts.push({ text: partText, isCorrect: isItemCorrect });
      }
    }

    const scoreRatio = totalCount > 0 ? correctCount / totalCount : 0;
    return {
      isCorrect: totalCount > 0 && correctCount === totalCount,
      question: 'Select question',
      studentAnswer: studentAnswers.join(', ') || 'No answer',
      correctAnswer: correctAnswers.join(', '),
      scoreRatio,
      answerParts
    };
  }

  private checkGroupingExercise(model: any, state: any): {
    isCorrect: boolean;
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    scoreRatio: number;
    answerParts?: ExamMistakeAnswerPart[];
  } {
    // model: { groups: [{ label: string, items: string[] }, ...] }
    // state: { itemOrder, groups: string[][] (item ids per group), items }
    const groups = model?.groups ?? [];
    const stateGroups = state?.groups ?? [];
    if (!state || !Array.isArray(stateGroups)) {
      return {
        isCorrect: false,
        question: 'Grouping',
        studentAnswer: 'No answer',
        correctAnswer: this.formatGroupingCorrectAnswer(groups),
        scoreRatio: 0,
        answerParts: [{ text: 'No answer', isCorrect: false }]
      };
    }

    let correctCount = 0;
    let totalCount = 0;
    const studentParts: string[] = [];
    const correctParts: string[] = [];
    const answerParts: ExamMistakeAnswerPart[] = [];

    for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
      const group = groups[groupIdx];
      const label = group.label ?? '';
      const items = group.items ?? [];
      for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
        const itemId = `${groupIdx}/${itemIdx}`;
        const itemLabel = items[itemIdx] ?? '';
        const correctGroupLabel = label;
        const studentGroupIdx = stateGroups.findIndex((g: readonly string[]) => g && g.includes(itemId));
        const studentGroupLabel = studentGroupIdx >= 0 && groups[studentGroupIdx] ? groups[studentGroupIdx].label : '—';
        const isCorrect = studentGroupIdx === groupIdx;
        if (isCorrect) correctCount++;
        totalCount++;
        const partText = `${itemLabel} → ${studentGroupLabel}`;
        studentParts.push(partText);
        correctParts.push(`${itemLabel} → ${correctGroupLabel}`);
        answerParts.push({ text: partText, isCorrect });
      }
    }

    const scoreRatio = totalCount > 0 ? correctCount / totalCount : 0;
    return {
      isCorrect: totalCount > 0 && correctCount === totalCount,
      question: 'Grouping',
      studentAnswer: studentParts.join(', ') || 'No answer',
      correctAnswer: correctParts.join(', '),
      scoreRatio,
      answerParts
    };
  }

  private formatGroupingCorrectAnswer(groups: Array<{ label: string; items: string[] }>): string {
    const parts: string[] = [];
    for (const group of groups) {
      const label = group.label ?? '';
      for (const item of group.items ?? []) {
        parts.push(`${item} → ${label}`);
      }
    }
    return parts.join(', ');
  }

  private extractSelectQuestionAnswers(model: any): string {
    const answers: string[] = [];
    for (const paragraph of model.paragraphs ?? []) {
      for (const item of paragraph.items ?? []) {
        if (item.type === 'select') {
          const correctIndex = item.choices?.findIndex((c: any) => c.selected) ?? -1;
          answers.push(item.choices?.[correctIndex]?.label ?? '?');
        }
      }
    }
    return answers.join(', ');
  }

  dispose() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.disposers.dispose();
  }
}
